import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, ProgressBar, Alert, Badge } from "react-bootstrap";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase.js";
import { Award, ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { questions, categories } from "../data/trainingQuestions.js";

export default function TrainingTestPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState(new Array(questions.length).fill(null));
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  const selectOption = (optionIndex) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = optionIndex;
    setUserAnswers(newAnswers);
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const submitTest = async () => {
    // Check for unanswered questions
    const unanswered = userAnswers.filter(answer => answer === null).length;

    if (unanswered > 0) {
      const message = language === "en"
        ? `You have ${unanswered} unanswered question(s). Do you want to submit anyway?`
        : `Você tem ${unanswered} pergunta(s) sem resposta. Deseja enviar mesmo assim?`;

      if (!window.confirm(message)) {
        return;
      }
    }

    setLoading(true);

    try {
      // Calculate results
      const calculatedResults = calculateResults();

      // Get attempt number
      const attemptsQuery = query(
        collection(db, "quizResults"),
        where("userId", "==", user.uid)
      );
      const attemptsSnap = await getDocs(attemptsQuery);
      const attemptNumber = attemptsSnap.docs.length + 1;

      // Save to Firebase
      const quizResult = {
        userId: user.uid,
        userName: user.displayName || user.name || user.email,
        userEmail: user.email,
        quizId: "quiz_health_safety_v1",
        quizTitle: "Health & Safety Training Assessment",
        quizVersion: "1.0",
        score: calculatedResults.score,
        totalQuestions: questions.length,
        percentage: parseFloat(calculatedResults.percentage),
        passed: calculatedResults.passed,
        attemptNumber: attemptNumber,
        startedAt: new Date(), // Could track actual start time
        completedAt: new Date(),
        answers: userAnswers.map((answer, index) => ({
          questionId: questions[index].id,
          selectedAnswer: answer,
          correctAnswer: questions[index].correct,
          isCorrect: answer === questions[index].correct
        })),
        categoryScores: calculatedResults.categoryScoresRaw
      };

      await addDoc(collection(db, "quizResults"), quizResult);

      // Show results
      setResults({...calculatedResults, attemptNumber});
      setShowResults(true);

    } catch (error) {
      console.error("Error submitting test:", error);
      const errorMessage = error?.message || error?.toString() || "Unknown error";
      alert(language === "en"
        ? `Error submitting test: ${errorMessage}. Please try again.`
        : `Erro ao enviar teste: ${errorMessage}. Por favor, tente novamente.`);
    } finally {
      setLoading(false);
    }
  };

  const calculateResults = () => {
    let correctAnswers = 0;
    const categoryScores = {};
    const categoryScoresRaw = {};

    // Initialize category scores
    const categoryList = language === "en"
      ? Object.values(categories.en)
      : Object.values(categories.pt);

    Object.keys(categories.en).forEach(key => {
      const catName = language === "en" ? categories.en[key] : categories.pt[key];
      categoryScores[catName] = { correct: 0, total: 0 };
      categoryScoresRaw[key] = { correct: 0, total: 0 };
    });

    // Calculate scores
    questions.forEach((question, index) => {
      const categoryName = language === "en"
        ? categories.en[question.category]
        : categories.pt[question.category];

      categoryScores[categoryName].total++;
      categoryScoresRaw[question.category].total++;

      if (userAnswers[index] === question.correct) {
        correctAnswers++;
        categoryScores[categoryName].correct++;
        categoryScoresRaw[question.category].correct++;
      }
    });

    const percentage = (correctAnswers / questions.length) * 100;
    const passed = percentage >= 85; // 85% passing threshold

    return {
      score: correctAnswers,
      total: questions.length,
      percentage: percentage.toFixed(1),
      passed: passed,
      categoryScores: categoryScores,
      categoryScoresRaw: categoryScoresRaw
    };
  };

  const retakeTest = () => {
    if (window.confirm(language === "en"
      ? "Are you sure you want to retake the test?"
      : "Tem certeza de que deseja refazer o teste?")) {
      setUserAnswers(new Array(questions.length).fill(null));
      setCurrentQuestionIndex(0);
      setShowResults(false);
      setResults(null);
    }
  };

  const goToHealthSafety = () => {
    navigate("/health-safety");
  };

  // Results Screen
  if (showResults && results) {
    const passed = results.passed;
    const icon = passed ? "✅" : "❌";
    const resultClass = passed ? "success" : "danger";

    return (
      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={goToHealthSafety}
              className="mb-3"
            >
              <ArrowLeft size={18} className="me-2" />
              {language === "en" ? "Back to Health & Safety" : "Voltar para Health & Safety"}
            </Button>

            <h2 className="fw-bold">
              <Award className="me-2" size={32} />
              {language === "en" ? "Test Results" : "Resultados do Teste"}
            </h2>
          </Col>
        </Row>

        <Row>
          <Col lg={8} className="mx-auto">
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <div style={{ fontSize: "80px" }}>{icon}</div>
                <h1 className={`text-${resultClass} mb-3`}>{results.percentage}%</h1>

                <Alert variant={resultClass} className="mb-4">
                  <h5>
                    {passed
                      ? (language === "en" ? "Congratulations! You Passed!" : "Parabéns! Você Passou!")
                      : (language === "en" ? "You did not pass. Please review and try again." : "Você não passou. Por favor, revise e tente novamente.")}
                  </h5>
                </Alert>

                <p className="mb-2">
                  <strong>{language === "en" ? "Score" : "Pontuação"}:</strong> {results.score}/{results.total}
                </p>
                <p className="mb-4">
                  <strong>{language === "en" ? "Attempt" : "Tentativa"}:</strong> #{results.attemptNumber}
                </p>

                <hr className="my-4" />

                <h5 className="mb-3">
                  {language === "en" ? "Performance by Category" : "Desempenho por Categoria"}
                </h5>

                <div className="text-start">
                  {Object.keys(results.categoryScores).map((category) => {
                    const score = results.categoryScores[category];
                    const percentage = (score.correct / score.total) * 100;

                    return (
                      <div key={category} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>{category}</span>
                          <strong>{score.correct}/{score.total}</strong>
                        </div>
                        <ProgressBar
                          now={percentage}
                          variant={percentage >= 85 ? "success" : percentage >= 70 ? "warning" : "danger"}
                        />
                      </div>
                    );
                  })}
                </div>

                <hr className="my-4" />

                <div className="d-flex gap-3 justify-content-center">
                  <Button variant="warning" size="lg" onClick={retakeTest}>
                    {language === "en" ? "Retake Test" : "Refazer Teste"}
                  </Button>
                  <Button variant="primary" size="lg" onClick={goToHealthSafety}>
                    {language === "en" ? "Back to Health & Safety" : "Voltar"}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Wrong Answers Section */}
        <Row className="mt-4">
          <Col lg={10} className="mx-auto">
            <Card className="shadow-sm">
              <Card.Header className="bg-danger text-white">
                <h5 className="mb-0">
                  {language === "en" ? "Review Incorrect Answers" : "Revisar Respostas Incorretas"}
                </h5>
              </Card.Header>
              <Card.Body>
                {questions.map((question, index) => {
                  const userAnswer = userAnswers[index];
                  const isCorrect = userAnswer === question.correct;

                  // Only show incorrect answers
                  if (isCorrect || userAnswer === null) return null;

                  const questionText = language === "en" ? question.question.en : question.question.pt;
                  const options = language === "en" ? question.options.en : question.options.pt;
                  const categoryName = language === "en" ? categories.en[question.category] : categories.pt[question.category];

                  return (
                    <div key={question.id} className="mb-4 pb-4 border-bottom">
                      <Badge bg="secondary" className="mb-2">{categoryName}</Badge>
                      <h6 className="fw-bold mb-3">
                        {language === "en" ? "Question" : "Pergunta"} {index + 1}: {questionText}
                      </h6>

                      <div className="mb-2">
                        <div className="p-2 bg-danger bg-opacity-10 border border-danger rounded mb-2">
                          <strong className="text-danger">
                            {language === "en" ? "Your answer:" : "Sua resposta:"}
                          </strong> {options[userAnswer]}
                        </div>

                        <div className="p-2 bg-success bg-opacity-10 border border-success rounded">
                          <strong className="text-success">
                            {language === "en" ? "Correct answer:" : "Resposta correta:"}
                          </strong> {options[question.correct]}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {questions.every((q, i) => userAnswers[i] === q.correct || userAnswers[i] === null) && (
                  <Alert variant="success" className="text-center">
                    <strong>
                      {language === "en"
                        ? "Perfect! You got all answered questions correct!"
                        : "Perfeito! Você acertou todas as perguntas respondidas!"}
                    </strong>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Test Screen
  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={goToHealthSafety}
            className="mb-3"
          >
            <ArrowLeft size={18} className="me-2" />
            {language === "en" ? "Back to Health & Safety" : "Voltar"}
          </Button>

          <div>
            <h2 className="fw-bold">
              <Award className="me-2" size={32} />
              Health & Safety Training
            </h2>
            <p className="text-muted">
              {language === "en" ? "Answer all 30 questions. You need 85% to pass." : "Responda todas as 30 perguntas. Você precisa de 85% para passar."}
            </p>
          </div>
        </Col>
      </Row>

      {/* Progress Bar */}
      <Row className="mb-4">
        <Col>
          <div className="mb-2 d-flex justify-content-between">
            <small className="text-muted">
              {language === "en" ? "Question" : "Pergunta"} {currentQuestionIndex + 1} {language === "en" ? "of" : "de"} {questions.length}
            </small>
            <small className="text-muted">
              {userAnswers.filter(a => a !== null).length}/{questions.length} {language === "en" ? "answered" : "respondidas"}
            </small>
          </div>
          <ProgressBar now={progress} style={{ height: "8px" }} />
        </Col>
      </Row>

      {/* Question Card */}
      <Row>
        <Col lg={10} className="mx-auto">
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <div className="mb-4">
                <Badge bg="secondary" className="mb-3">
                  {language === "en" ? categories.en[currentQuestion.category] : categories.pt[currentQuestion.category]}
                </Badge>
                <h4 className="fw-bold">
                  {language === "en" ? currentQuestion.question.en : currentQuestion.question.pt}
                </h4>
              </div>

              <div className="d-grid gap-3">
                {(language === "en" ? currentQuestion.options.en : currentQuestion.options.pt).map((option, index) => {
                  const isSelected = userAnswers[currentQuestionIndex] === index;

                  return (
                    <div
                      key={index}
                      className={`p-3 border rounded cursor-pointer ${isSelected ? "border-primary bg-light" : "border-secondary"}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectOption(index)}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className={`rounded-circle me-3 ${isSelected ? "bg-primary" : "bg-white border"}`}
                          style={{
                            width: "24px",
                            height: "24px",
                            minWidth: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {isSelected && <CheckCircle size={16} className="text-white" />}
                        </div>
                        <span className={isSelected ? "fw-bold" : ""}>{option}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Navigation Buttons */}
              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <Button
                  variant="outline-secondary"
                  onClick={previousQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  <ArrowLeft size={18} className="me-2" />
                  {language === "en" ? "Previous" : "Anterior"}
                </Button>

                {currentQuestionIndex === questions.length - 1 ? (
                  <Button
                    variant="success"
                    size="lg"
                    onClick={submitTest}
                    disabled={loading}
                  >
                    {loading ? (language === "en" ? "Submitting..." : "Enviando...") : (language === "en" ? "Submit Test" : "Enviar Teste")}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={nextQuestion}
                  >
                    {language === "en" ? "Next" : "Próximo"}
                    <ArrowRight size={18} className="ms-2" />
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
